/**
 * Enhanced Swiper integration for Shopify Theme Editor
 * 
 * Automatically enhances all Swiper instances with theme editor functionality:
 * - Section reload handling
 * - Block selection navigation
 * - Autoplay management during editing
 * - Proper cleanup
 * 
 * Only runs in theme editor (Shopify.designMode) for zero production impact
 */

class SwiperThemeEditor {
    constructor() {
        this.swiperInstances = new Map();
        this.init();
    }

    init() {
        // Only run in theme editor
        if (!window.Shopify || !window.Shopify.designMode) {
            return;
        }

        // Wait for theme to be loaded
        document.addEventListener('theme:loaded', () => {
            this.setupEventListeners();
            this.enhanceExistingSwipers();
        });
    }

    setupEventListeners() {
        // Section reload - reinitialize swipers
        document.addEventListener('shopify:section:load', (event) => {
            setTimeout(() => {
                this.enhanceSectionSwipers(event.target);
            }, 100);
        });

        // Block selection - navigate to selected slide
        document.addEventListener('shopify:block:select', (event) => {
            this.handleBlockSelect(event);
        });

        // Section selection - pause autoplay
        document.addEventListener('shopify:section:select', (event) => {
            this.pauseSectionSwipers(event.target);
        });

        // Section deselection - resume autoplay
        document.addEventListener('shopify:section:deselect', (event) => {
            this.resumeSectionSwipers(event.target);
        });

        // Section unload - cleanup
        document.addEventListener('shopify:section:unload', (event) => {
            this.cleanupSectionSwipers(event.target);
        });
    }

    enhanceExistingSwipers() {
        // Find all existing swiper containers and enhance them
        document.querySelectorAll('.swiper').forEach(container => {
            this.enhanceSwiper(container);
        });
    }

    enhanceSectionSwipers(sectionElement) {
        // Enhance all swipers within a specific section
        const swiperContainers = sectionElement.querySelectorAll('.swiper');
        swiperContainers.forEach(container => {
            this.enhanceSwiper(container);
        });
    }

    enhanceSwiper(container) {
        // Skip if already enhanced
        if (container.hasAttribute('data-theme-editor-enhanced')) {
            return;
        }

        // Mark as enhanced
        container.setAttribute('data-theme-editor-enhanced', 'true');

        // Store reference to swiper instance when it's created
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    // Check if swiper has been initialized (has swiper-container class)
                    if (container.classList.contains('swiper-initialized')) {
                        this.registerSwiperInstance(container);
                        observer.disconnect();
                    }
                }
            });
        });

        observer.observe(container, {
            attributes: true,
            attributeFilter: ['class']
        });

        // Also check if already initialized
        if (container.classList.contains('swiper-initialized')) {
            this.registerSwiperInstance(container);
        }
    }

    registerSwiperInstance(container) {
        // Get the swiper instance
        const swiper = container.swiper;
        if (swiper) {
            const sectionId = this.getSectionId(container);
            if (sectionId) {
                if (!this.swiperInstances.has(sectionId)) {
                    this.swiperInstances.set(sectionId, []);
                }
                this.swiperInstances.get(sectionId).push({
                    container,
                    swiper,
                    originalAutoplay: swiper.autoplay && swiper.autoplay.running
                });
            }
        }
    }

    handleBlockSelect(event) {
        const blockElement = event.target;
        const sectionElement = blockElement.closest('.shopify-section');
        
        if (!sectionElement) return;

        const sectionId = this.getSectionId(sectionElement);
        const swiperInstances = this.swiperInstances.get(sectionId);

        if (!swiperInstances) return;

        swiperInstances.forEach(({ container, swiper }) => {
            // Find the slide containing this block
            const slides = container.querySelectorAll('.swiper-slide');
            const targetSlide = Array.from(slides).find(slide => {
                // Check if slide contains the block element
                return slide.contains(blockElement) || 
                       slide === blockElement ||
                       slide.querySelector(`[data-block-id="${blockElement.id}"]`) ||
                       slide.hasAttribute('data-block-id') && slide.getAttribute('data-block-id') === blockElement.id;
            });

            if (targetSlide && swiper) {
                const slideIndex = Array.from(slides).indexOf(targetSlide);
                
                // Pause autoplay temporarily
                if (swiper.autoplay && swiper.autoplay.running) {
                    swiper.autoplay.stop();
                }

                // Navigate to slide
                swiper.slideTo(slideIndex);

                // Resume autoplay after delay
                setTimeout(() => {
                    if (swiper.autoplay && swiper.autoplay.start) {
                        swiper.autoplay.start();
                    }
                }, 2000);
            }
        });
    }

    pauseSectionSwipers(sectionElement) {
        const sectionId = this.getSectionId(sectionElement);
        const swiperInstances = this.swiperInstances.get(sectionId);

        if (!swiperInstances) return;

        swiperInstances.forEach(({ swiper }) => {
            if (swiper && swiper.autoplay && swiper.autoplay.running) {
                swiper.autoplay.stop();
            }
        });
    }

    resumeSectionSwipers(sectionElement) {
        const sectionId = this.getSectionId(sectionElement);
        const swiperInstances = this.swiperInstances.get(sectionId);

        if (!swiperInstances) return;

        swiperInstances.forEach(({ swiper, originalAutoplay }) => {
            if (swiper && swiper.autoplay && originalAutoplay) {
                swiper.autoplay.start();
            }
        });
    }

    cleanupSectionSwipers(sectionElement) {
        const sectionId = this.getSectionId(sectionElement);
        const swiperInstances = this.swiperInstances.get(sectionId);

        if (!swiperInstances) return;

        swiperInstances.forEach(({ container, swiper }) => {
            // Remove enhancement marker
            container.removeAttribute('data-theme-editor-enhanced');
            
            // Destroy swiper if it has destroy method
            if (swiper && swiper.destroy) {
                swiper.destroy(true, true);
            }
        });

        // Remove from registry
        this.swiperInstances.delete(sectionId);
    }

    getSectionId(element) {
        const sectionElement = element.classList.contains('shopify-section') 
            ? element 
            : element.closest('.shopify-section');
        
        if (sectionElement && sectionElement.id) {
            return sectionElement.id.replace('shopify-section-', '');
        }
        return null;
    }
}

// Initialize the swiper theme editor enhancement
new SwiperThemeEditor();